import React, { useEffect, useState } from 'react';
import { fetchReportImage } from './attendanceApi';

const green = { border: 'none', borderRadius: 10, background: '#25d366', color: '#fff', fontWeight: 800, cursor: 'pointer' };
const grey = { border: 'none', borderRadius: 10, background: '#e5e7eb', fontWeight: 700, cursor: 'pointer' };

// "Generate Image" tab: preview the PNG, then share it (Web Share API) or download it.
export default function ReportImagePanel({ user, className, date, templateId, body }) {
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');

  const filename = `attendance-${className}-${date}.png`;

  const generate = async () => {
    if (!templateId && !body) { setBlob(null); setNote('Select a template to generate the image'); return; }
    setLoading(true); setNote('');
    try { setBlob(await fetchReportImage(user, className, date, templateId, body)); }
    catch (e) { setBlob(null); setNote(e.response?.data?.error || 'Could not generate image'); }
    finally { setLoading(false); }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, [className, date, templateId, body]);

  // Object URL for the <img>; revoked when replaced/unmounted to avoid leaks.
  useEffect(() => {
    if (!blob) { setUrl(''); return undefined; }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  const file = blob ? new File([blob], filename, { type: 'image/png' }) : null;
  const hasBridge = !!(window.Android && typeof window.Android.shareImage === 'function');
  const canShareFile = hasBridge || !!(file && navigator.canShare && navigator.canShare({ files: [file] }));

  const download = () => {
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const share = async () => {
    try {
      // Optional native bridge: if your Android app exposes window.Android.shareImage(base64, filename), prefer it.
      if (window.Android && typeof window.Android.shareImage === 'function') {
        const dataUrl = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(blob); });
        window.Android.shareImage(String(dataUrl).split(',')[1], filename);
        return;
      }
      await navigator.share({ files: [file], title: 'Attendance Report', text: `Attendance – Class ${className} – ${date}` });
    } catch (e) {
      if (e.name !== 'AbortError') { setNote('Sharing failed. Image downloaded instead.'); download(); }
    }
  };

  return (
    <div>
      {loading && <div style={{ fontSize: 13, padding: 20, textAlign: 'center' }}>Generating image…</div>}
      {url && !loading && (
        <img src={url} alt="Attendance report" style={{ width: '100%', borderRadius: 10, border: '1px solid #e5e7eb' }} />
      )}
      {blob && !loading && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          {canShareFile && <button onClick={share} style={{ ...green, flex: 1, padding: 12, fontSize: 15 }}>📤 Share Image</button>}
          <button onClick={download} style={{ ...(canShareFile ? grey : green), flex: 1, padding: 12, fontSize: 15 }}>⬇️ Download Image</button>
        </div>
      )}
      {blob && !canShareFile && !loading && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
          This browser can't share files directly. Download the image and attach it in WhatsApp.
        </div>
      )}
      {!loading && <button onClick={generate} style={{ ...grey, marginTop: 8, padding: '6px 12px', fontSize: 12 }}>🔄 Regenerate</button>}
      {note && <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{note}</div>}
    </div>
  );
}
